import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AssignmentTurnedInRounded from '@mui/icons-material/AssignmentTurnedInRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import LibraryBooksRounded from '@mui/icons-material/LibraryBooksRounded';
import RateReviewRounded from '@mui/icons-material/RateReviewRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import { Link as RouterLink, useNavigate } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { StatTile } from '@/components/data/StatTile';
import { useAsync } from '@/hooks/useAsync';
import { useSession } from '@/platform/session';
import { formatPercent, formatRelative } from '@/utils/format';
import { myPerformance, myQuizzes, staffDashboard } from '../api';
import { AttemptStatusChip } from './bits';
import { PerformanceList } from './charts';
import { QuizCard } from './QuizCard';

export function HomeStaff() {
  const navigate = useNavigate();
  // Teachers only look: no shortcuts to the bank, grading or new questions.
  const { isManager } = useSession();
  const { data, loading, error } = useAsync(staffDashboard);

  return (
    <Stack spacing={3}>
      {error ? (
        <Alert severity="error">Não foi possível carregar o resumo de Avaliações.</Alert>
      ) : null}
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Questões no banco"
            value={data?.questions ?? 0}
            icon={LibraryBooksRounded}
            loading={loading}
            onClick={isManager ? () => void navigate('/assessments/questions') : undefined}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Avaliações abertas"
            value={data?.quizzesOpen ?? 0}
            icon={EventAvailableRounded}
            loading={loading}
            onClick={() => void navigate('/assessments')}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Respostas para corrigir"
            value={data?.pendingReview ?? 0}
            icon={RateReviewRounded}
            loading={loading}
            onClick={isManager ? () => void navigate('/assessments/grading') : undefined}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile
            label="Média geral"
            value={formatPercent(data?.averagePercent)}
            icon={TrendingUpRounded}
            loading={loading}
            onClick={() => void navigate('/assessments/reports')}
          />
        </Grid>
      </Grid>

      <Card>
        <Stack
          direction="row"
          sx={{
            px: 2.5,
            py: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" component="h2">
            Envios recentes
          </Typography>
          {isManager && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<AddRounded />}
                component={RouterLink}
                to="/assessments/questions?new=1"
              >
                Nova questão
              </Button>
            </Stack>
          )}
        </Stack>
        {loading ? (
          <Box sx={{ p: 2.5 }}>
            <Skeleton height={32} />
            <Skeleton height={32} />
          </Box>
        ) : data && data.recent.length > 0 ? (
          data.recent.map((r) => (
            <Box
              key={r.attemptId}
              component={RouterLink}
              to={`/assessments/attempts/${r.attemptId}/review`}
              sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr auto 72px' },
                gap: { xs: 0.5, sm: 2 },
                alignItems: 'center',
                px: 2.5,
                py: 1.5,
                color: 'inherit',
                textDecoration: 'none',
                '& + &': { borderTop: `1px solid ${theme.vars.palette.divider}` },
                '&:hover': { bgcolor: theme.alpha(theme.vars.palette.primary.main, 0.04) },
              })}
            >
              <Typography variant="subtitle2" noWrap>
                {r.studentName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                {r.quizTitle} · {formatRelative(r.submittedAt)}
              </Typography>
              <AttemptStatusChip status={r.status} />
              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: 'right',
                  display: { xs: 'none', sm: 'block' },
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {r.status === 'graded' ? formatPercent(r.percent) : '—'}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            Nenhum envio ainda.
          </Typography>
        )}
      </Card>
    </Stack>
  );
}

export function HomeStudent() {
  const quizzes = useAsync(myQuizzes);
  const performance = useAsync(myPerformance);
  const todo = (quizzes.data ?? []).filter(
    (q) => q.state === 'available' || q.state === 'in_progress',
  );
  const upcoming = (quizzes.data ?? []).filter((q) => q.state === 'upcoming');
  const categories = (performance.data?.categories ?? []).filter(
    (c) => c.path.split(' › ').length === 1,
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
        >
          <Typography variant="h6" component="h2">
            Para fazer
          </Typography>
          <Link component={RouterLink} to="/assessments" variant="body2">
            Ver todas
          </Link>
        </Stack>
        {quizzes.loading ? (
          <Skeleton variant="rounded" height={140} />
        ) : todo.length === 0 ? (
          <Card>
            <EmptyState
              compact
              icon={AssignmentTurnedInRounded}
              title="Tudo em dia!"
              description={
                upcoming.length > 0
                  ? `Você tem ${upcoming.length} avaliação(ões) programada(s) para os próximos dias.`
                  : 'Nenhuma avaliação pendente no momento.'
              }
            />
          </Card>
        ) : (
          <Grid container spacing={2}>
            {todo.map((quiz) => (
              <Grid key={quiz.id} size={{ xs: 12, md: 6 }}>
                <QuizCard quiz={quiz} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {performance.data && performance.data.summary.quizzes > 0 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Sua média
                </Typography>
                <Typography variant="h2" component="p" sx={{ my: 1 }}>
                  {formatPercent(performance.data.summary.average)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={performance.data.summary.average ?? 0}
                  color="lime"
                  sx={{ height: 8 }}
                  aria-label="Sua média"
                />
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
                  {performance.data.summary.quizzes} avaliação(ões) concluída(s)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
                  Seu desempenho por área
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  A marca vertical mostra a média da turma.
                </Typography>
                <PerformanceList
                  compareLabel="Média da turma"
                  empty="Os resultados por área aparecem quando as notas forem liberadas."
                  rows={categories.map((c) => ({
                    key: c.id,
                    label: c.name,
                    percent: c.percent,
                    compare: c.classPercent,
                  }))}
                />
                <Button
                  component={RouterLink}
                  to="/assessments"
                  endIcon={<ArrowForwardRounded />}
                  sx={{ mt: 2 }}
                >
                  Ver meus resultados
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Stack>
  );
}
